(() => {
  if (customElements.get('dvalia-cart')) return;

  class DvaliaCart extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.dialog = this.querySelector('[data-dv-cart-dialog]');
      this.body = this.querySelector('[data-dv-cart-body]');
      this.errorRegion = this.querySelector('[data-dv-cart-error]');
      this.status = this.querySelector('[data-dv-cart-status]');
      this.progress = this.querySelector('[data-dv-cart-progress]');
      this.dataset.cartReady = 'true';
      this.busy = false;
      this.disabledControls = new Map();
      const options = { signal: this.controller.signal };
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest?.('a[data-dv-cart-trigger]');
        if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger).catch(() => {});
      }, options);
      this.addEventListener('click', (event) => {
        if (event.target.closest?.('[data-dv-cart-close]')) this.close();
        const stepButton = event.target.closest?.('[data-dv-cart-step]');
        if (stepButton) {
          event.preventDefault();
          if (this.busy || stepButton.disabled) return;
          const input = stepButton.closest('[data-dv-cart-line]')?.querySelector('[data-dv-cart-quantity]');
          if (!input) return;
          const step = Number(input.step) || 1;
          const quantity = Number(input.defaultValue) + (stepButton.dataset.dvCartStep === 'increase' ? step : -step);
          if (!Number.isSafeInteger(quantity) || quantity < Number(input.min) || (input.max && quantity > Number(input.max))) return;
          this.rememberControl(stepButton, stepButton.dataset.dvCartStep);
          this.changeItem(input.dataset.lineKey, quantity).catch(() => {});
          return;
        }
        const remove = event.target.closest?.('[data-dv-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        if (!this.busy) {
          this.rememberControl(remove, 'remove');
          this.changeItem(remove.dataset.lineKey, 0).catch(() => {});
        }
      }, options);
      this.addEventListener('change', (event) => {
        const input = event.target.closest?.('[data-dv-cart-quantity]');
        if (!input || this.busy) return;
        const quantity = Number(input.value);
        if (!Number.isSafeInteger(quantity) || !input.checkValidity()) {
          input.reportValidity();
          input.value = input.defaultValue;
          return;
        }
        this.rememberControl(input, 'quantity');
        this.changeItem(input.dataset.lineKey, quantity).catch(() => {});
      }, options);
      this.addEventListener('submit', (event) => {
        if (event.submitter?.name !== 'update') return;
        event.preventDefault();
        if (this.busy) return;
        const input = [...event.target.querySelectorAll('[data-dv-cart-quantity]')].find((field) => field.value !== field.defaultValue);
        if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, options);
      this.dialog.addEventListener('close', () => this.restoreFocus(), options);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.restoreFocus();
    }

    get cartUrl() {
      return new URL(this.dataset.cartUrl, window.location.origin);
    }

    endpoint(action) {
      const url = this.cartUrl;
      url.pathname = `${url.pathname.replace(/\/$/, '')}/${action}.js`;
      return url;
    }

    async request(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: this.controller.signal,
        headers: { Accept: 'application/json', ...options.headers },
      });
      const payload = await response.json().catch(() => null);
      return { response, payload };
    }

    message(payload) {
      return typeof payload?.description === 'string' ? payload.description :
        typeof payload?.message === 'string' ? payload.message : this.dataset.errorMessage;
    }

    setError(message = '') {
      this.errorRegion.textContent = message;
      this.errorRegion.hidden = !message;
    }

    rememberControl(control, action) {
      const line = control.closest('[data-dv-cart-line]');
      if (!line) return;
      this.focusControl = {
        key: line.dataset.lineKey,
        identity: line.dataset.lineIdentity,
        index: [...this.body.querySelectorAll('[data-dv-cart-line]')].indexOf(line),
        action,
      };
    }

    restoreControl() {
      const focus = this.focusControl;
      this.focusControl = null;
      if (!focus || !this.dialog.open) return;
      const lines = [...this.body.querySelectorAll('[data-dv-cart-line]')];
      const line = lines.find((item) => item.dataset.lineKey === focus.key) ||
        lines.find((item) => item.dataset.lineIdentity === focus.identity) ||
        lines[Math.min(focus.index, lines.length - 1)];
      const selector = focus.action === 'remove' ? '[data-dv-cart-remove]' :
        focus.action === 'quantity' ? '[data-dv-cart-quantity]' : `[data-dv-cart-step="${focus.action}"]`;
      const target = line?.querySelector(selector);
      const fallback = line?.querySelector('[data-dv-cart-quantity]') || this.body.querySelector('a') || this.querySelector('[data-dv-cart-close]');
      (target && !target.disabled ? target : fallback)?.focus({ preventScroll: true });
    }

    setBusy(value) {
      this.busy = value;
      this.body.setAttribute('aria-busy', String(value));
      this.progress.hidden = !value;
      this.status.textContent = value ? this.dataset.busyMessage : this.errorRegion.hidden ? this.dataset.updatedMessage : '';
      this.body.querySelectorAll('[data-dv-cart-remove]').forEach((control) => {
        if (value) control.setAttribute('aria-disabled', 'true');
        else control.removeAttribute('aria-disabled');
      });
      if (value) {
        this.disabledControls.clear();
        this.body.querySelectorAll('button, input, select').forEach((control) => {
          this.disabledControls.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        this.disabledControls.forEach((disabled, control) => {
          if (control.isConnected) control.disabled = disabled;
        });
        this.disabledControls.clear();
        this.body.querySelectorAll('dvalia-mask-upsell').forEach((offer) => offer.updateAvailability?.());
        this.restoreControl();
      }
    }

    show(trigger) {
      if (this.dialog.open) return;
      this.returnFocus = trigger || document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    restoreFocus() {
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    async open(trigger) {
      this.show(trigger);
      if (this.busy) return;
      this.setError();
      this.setBusy(true);
      try {
        await this.refresh();
      } catch (error) {
        if (error.name !== 'AbortError') this.fallback();
        throw error;
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    async addItems(items) {
      if (this.dataset.salesLocked !== 'false') throw new Error(this.dataset.prelaunchMessage);
      if (!Array.isArray(items) || !items.length || items.some((item) => !Number.isSafeInteger(Number(item.id)) || Number(item.id) <= 0 || !Number.isSafeInteger(Number(item.quantity)) || Number(item.quantity) <= 0)) {
        throw new Error(this.dataset.errorMessage);
      }
      this.lastAddedVariantId = Number(items[0].id);
      return this.mutate('add', { items: items.map(({ id, quantity }) => ({ id: Number(id), quantity: Number(quantity) })) });
    }

    async changeItem(key, quantity) {
      if (!key || !Number.isSafeInteger(quantity) || quantity < 0) throw new Error(this.dataset.errorMessage);
      return this.mutate('change', { id: key, quantity });
    }

    async mutate(action, data) {
      if (this.busy) throw new Error(this.dataset.busyMessage);
      this.setError();
      this.setBusy(true);
      let result;
      let mutationError;
      try {
        result = await this.request(this.endpoint(action), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, sections: this.dataset.sectionId, sections_url: this.cartUrl.pathname }),
        });
        if (!result.response.ok) mutationError = new Error(this.message(result.payload));
      } catch (error) {
        mutationError = error;
      }
      try {
        if (mutationError?.name === 'AbortError') throw mutationError;
        const cart = await this.refresh(result?.payload);
        this.show();
        if (mutationError) {
          this.setError(mutationError.message || this.dataset.errorMessage);
          throw mutationError;
        }
        return cart;
      } catch (error) {
        if (error !== mutationError && error.name !== 'AbortError') this.fallback();
        throw error;
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    async refresh(payload) {
      const cartJsonUrl = this.cartUrl;
      cartJsonUrl.pathname = `${cartJsonUrl.pathname.replace(/\/$/, '')}.js`;
      const sectionId = this.dataset.sectionId;
      const existingHtml = payload?.sections?.[sectionId];
      const sectionUrl = this.cartUrl;
      sectionUrl.searchParams.set('sections', sectionId);
      const htmlPromise = typeof existingHtml === 'string' && existingHtml.trim() ? Promise.resolve(existingHtml) :
        this.request(sectionUrl, { headers: { Accept: 'text/html' } }).then((result) => {
          if (!result.response.ok) throw new Error(this.dataset.errorMessage);
          return result.payload?.[sectionId];
        });
      const [cartResult, html] = await Promise.all([this.request(cartJsonUrl), htmlPromise]);
      if (!cartResult.response.ok || !cartResult.payload || typeof cartResult.payload.item_count !== 'number') throw new Error(this.dataset.errorMessage);
      const source = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-dv-cart-content]') : null;
      if (!source) throw new Error(this.dataset.errorMessage);
      this.body.replaceChildren(source.cloneNode(true));
      const pageContent = document.querySelector('.dv-cart > [data-dv-cart-content]');
      if (pageContent) {
        const copy = source.cloneNode(true);
        copy.dataset.cartContext = 'page';
        copy.querySelector('[data-dv-mask-upsell], dvalia-mask-upsell')?.remove();
        copy.querySelectorAll('[id]').forEach((element) => { element.id = `Page-${element.id}`; });
        copy.querySelectorAll('label[for]').forEach((label) => { label.htmlFor = `Page-${label.htmlFor}`; });
        copy.querySelectorAll('[data-dv-cart-step]').forEach((button) => button.remove());
        copy.querySelectorAll('.dv-cart-quantity').forEach((group) => {
          group.classList.remove('dv-cart-quantity');
          group.removeAttribute('role');
        });
        pageContent.replaceWith(copy);
      }
      const cart = cartResult.payload;
      document.querySelectorAll('[data-dv-cart-count]').forEach((counter) => {
        counter.textContent = String(cart.item_count);
        counter.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-dv-cart-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-label', this.dataset.cartCountLabel.replace('CART_COUNT', cart.item_count));
      });
      this.dispatchEvent(new CustomEvent('dvalia:cart-updated', { bubbles: true, detail: { cart } }));
      return cart;
    }

    fallback() {
      window.location.assign(this.cartUrl.href);
    }
  }

  customElements.define('dvalia-cart', DvaliaCart);

  class DvaliaMaskUpsell extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.cart = this.closest('dvalia-cart');
      this.select = this.querySelector('[data-dv-mask-choice]');
      this.button = this.querySelector('[data-dv-mask-add]');
      try {
        this.data = JSON.parse(this.querySelector('[data-dv-mask-data]').textContent);
        this.matches = JSON.parse(this.data.matches || '{}') || {};
      } catch { return; }
      this.variants = this.data.mask.variants.filter(variant =>
        Number.isSafeInteger(variant.id) && variant.id > 0 && variant.price === this.data.expectedPrice
      ).sort((a, b) => Number(b.available && b.stock >= 1) - Number(a.available && a.stock >= 1));
      const available = this.variants.filter(variant => variant.available && variant.stock >= 1);
      if (!this.cart || !available.length) return;
      const pillowcase = this.data.pillowcases.find(item => item.id === this.cart.lastAddedVariantId) || this.data.pillowcases[0];
      const matchedId = Number(this.matches[pillowcase?.colour]);
      const match = available.find(variant => variant.id === matchedId);
      this.select.replaceChildren(...this.variants.map(variant => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.disabled = !variant.available || variant.stock < 1;
        option.textContent = (variant.colourLabel || variant.colour) + (option.disabled ? " — " + this.data.soldOut : "");
        return option;
      }));
      this.select.value = String((match || available[0]).id);
      this.pending = false;
      const render = () => {
        const variant = this.variants.find(item => item.id === Number(this.select.value));
        if (!variant) { this.button.disabled = true; return; }
        this.querySelector('[data-dv-mask-support]').textContent = variant.id === match?.id ? this.data.matching : this.data.neutral;
        const image = this.querySelector('[data-dv-mask-image]');
        const imageUrl = new URL(variant.image, window.location.origin);
        imageUrl.searchParams.set('width', '152');
        image.src = imageUrl.href;
        image.alt = this.data.imageAlt.replace('__colour__', variant.colourLabel || variant.colour);
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: this.data.currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
        }).format(variant.price / 100);
        this.querySelector('[data-dv-mask-price]').textContent = '+ ' + (this.data.currency === 'NOK' ? new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(variant.price / 100) + ' kr' : money);
        this.button.disabled = !variant.available || variant.stock < 1 || this.data.preview || this.pending || this.cart.busy;
      };
      this.updateAvailability = render;
      this.select.addEventListener('change', render, { signal: this.controller.signal });
      this.button.addEventListener('click', async () => {
        if (this.pending || this.data.preview || this.cart.busy) return;
        const variant = this.variants.find(item => item.id === Number(this.select.value));
        if (!variant || !variant.available || variant.stock < 1) return;
        this.pending = true;
        this.button.disabled = true;
        try {
          await this.cart.addItems([{ id: variant.id, quantity: 1 }]);
        } catch (error) {
          if (error.name !== 'AbortError') this.cart.setError(error.message || this.data.error);
        } finally {
          if (this.isConnected) { this.pending = false; render(); }
        }
      }, { signal: this.controller.signal });
      render();
      this.hidden = false;
    }
    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('dvalia-mask-upsell')) customElements.define('dvalia-mask-upsell', DvaliaMaskUpsell);

})();

